import { useToggle } from 'react-use';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { useSafetyMessages } from '../../../hooks/useSafetyMessages';
import { useSearchParams } from 'react-router';
import ICSWidget from '../ICSWidget';
import { ConditionalCollapsibleCard } from 'components/IAP/ConditionalCollapsibleCard';
import {
  useFutureOperationalPeriodNumber,
  useIncidentPermissions,
} from '../../../hooks/incidentHooks';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import SafetyPlanFormCard from './SafetyPlanFormCard';
import { getIncidentCardTitle } from '../../USCGPage';

const ICS_208_EXPORT_ROLES = ['IC/UC', 'PSC', 'Situation Unit Leader'];

const SafetyPlan = () => {
  const [searchParams] = useSearchParams();
  const phase = searchParams.get('phase');
  const mode = searchParams.get('mode');
  const sectionKey = `${phase}-${mode}-safety-plan`;
  const [isAdding, toggleIsAdding] = useToggle(false);

  const { data: safetyMessages, isLoading, error } = useSafetyMessages();
  const { exportAsForm } = useIcsFormPdfPreview();
  const futurePeriodNumber = useFutureOperationalPeriodNumber();

  const { data: incidentPermissions } = useIncidentPermissions();
  const canEditCards = incidentPermissions?.['incident:editCards'] ?? false;

  const filteredSafetyMessage = safetyMessages?.[0] ?? null;

  const handleDismissCreate = () => {
    toggleIsAdding(false);
  };

  const allowWorkspaceDownload = !!filteredSafetyMessage && !isAdding && !error;

  const handleExport = () => {
    void exportAsForm(
      {
        formname: 'ICS_208_CG',
        data: {},
        mapSketch: null,
        periodNumber: futurePeriodNumber,
      },
      { allowWorkspaceDownload }
    );
  };

  return (
    <ConditionalCollapsibleCard
      sectionKey={sectionKey}
      className="planning-widget"
      contentClassName="content mt-2"
      headerLeft={
        <ICSWidget.HeaderLeft
          iconName="shield"
          iconColor="primary"
          title={getIncidentCardTitle('safetyPlan')}
          subtitle="Comprehensive safety planning and hazard mitigation"
        />
      }
      headerRight={
        <ICSWidget.HeaderRight
          reviewActionKey="safetyMessages"
          icsRoles={ICS_208_EXPORT_ROLES}
          handleExport={handleExport}
          exportText="Preview ICS-208"
        />
      }
    >
      {isLoading && (
        <div className="py-4 text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-danger">
          Error loading safety plans: {error.message}
        </div>
      )}

      {!isLoading && !error && filteredSafetyMessage && (
        <SafetyPlanFormCard
          mode="edit"
          safetyMessage={filteredSafetyMessage}
          readOnly={!canEditCards}
        />
      )}

      {!isLoading && !error && !filteredSafetyMessage && !isAdding && (
        <>
          {canEditCards && (
            <StylishNewButton
              className="button button--primary mb-3"
              onClick={(e) => {
                e.stopPropagation();
                toggleIsAdding(true);
              }}
            >
              <SharedIcon iconName="add" classes="me-2" />
              Add Safety Plan
            </StylishNewButton>
          )}
          <div className="section-main d-flex flex-column align-items-center">
            <div className="d-flex justify-content-center">
              <SharedIcon iconName="shield" classes="me-2" size="lg" />
            </div>
            <div>
              <div className="fs-5 mb-2">No Safety Plans</div>
              <div className="mb-2">No safety plans have been created yet</div>
              <div>
                Create your first safety plan to get started with comprehensive
                safety planning including hazard analysis, safety protocols,
                emergency procedures, and risk mitigation strategies.
              </div>
            </div>
          </div>
        </>
      )}

      {!isLoading &&
        !error &&
        !filteredSafetyMessage &&
        isAdding &&
        canEditCards && (
          <SafetyPlanFormCard
            mode="create"
            onDismissCreate={handleDismissCreate}
          />
        )}
    </ConditionalCollapsibleCard>
  );
};

export default SafetyPlan;
